import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ContributorCategoryToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useContributorCategoryMutations } from '~/hooks/use-contributor-categories';
import {
	contributorCategorySchema,
	EMPTY_CONTRIBUTOR_CATEGORY,
	tokenToForm,
	type ContributorCategoryFormValues
} from '~/lib/schemas/contributor-category';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Raw token being edited, or null for a new row. */
	initial: ContributorCategoryToken | null;
}

/** Add / edit a contributor category (Κατηγορία Συντελεστή). */
export function ContributorCategoryEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useContributorCategoryMutations();
	const form = useForm<ContributorCategoryFormValues>({
		resolver: zodResolver(contributorCategorySchema) as Resolver<ContributorCategoryFormValues>,
		defaultValues: initial ? tokenToForm(initial) : EMPTY_CONTRIBUTOR_CATEGORY,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);
	const chex = watch('chex');

	useEffect(() => {
		if (open) reset(initial ? tokenToForm(initial) : EMPTY_CONTRIBUTOR_CATEGORY);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync({ form: values, original: initial });
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('contributors.categories.editTitle') : t('contributors.categories.addTitle')}</DialogTitle>
					<DialogDescription>{t('contributors.categories.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-3">
						<FormField label={t('contributors.categories.columns.name')} error={formState.errors.nm?.message}>
							<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
						</FormField>
						<FormField label={t('contributors.categories.columns.code')} error={formState.errors.cd?.message}>
							<Input {...register('cd')} maxLength={50} aria-invalid={Boolean(formState.errors.cd)} />
						</FormField>
					</div>
					<FormField label={t('contributors.categories.columns.color')} error={formState.errors.chex?.message}>
						<div className="flex items-center gap-2">
							<input
								type="color"
								value={chex || '#000000'}
								onChange={e => setValue('chex', e.target.value, { shouldDirty: true })}
								className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface p-1"
								aria-label={t('contributors.categories.columns.color')}
							/>
							<Input
								value={chex ?? ''}
								onChange={e => setValue('chex', e.target.value || null, { shouldDirty: true })}
								placeholder="#RRGGBB"
								className="max-w-40"
							/>
						</div>
					</FormField>
					<div className="grid grid-cols-3 gap-3">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="cc-prd">{t('contributors.categories.columns.assignToProduct')}</Label>
							<Switch
								id="cc-prd"
								checked={watch('ass_to_prd')}
								onCheckedChange={next => setValue('ass_to_prd', next, { shouldDirty: true })}
							/>
						</div>
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="cc-iv">{t('contributors.categories.columns.visible')}</Label>
							<Switch id="cc-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
						</div>
						<FormField label={t('contributors.categories.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
